/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { Grid, Form, Button, Icon, Modal } from '@mairistem/composants';
import {
  EtatCivil,
  SectionHeader,
  TypeComponent,
} from '@mairistem/administres-utils';
import { request } from '@mairistem/tools';

import { ChampLieu, ChampProfession } from '@/components/Common';
import {
  buildIndividuPutPayload,
  buildParentUpdatePayload,
  parseDateNaissance,
  parseIndividuFiche,
} from '@/utils';
import { validationInformationsNaissance } from '@/utils/validations';

interface ModalParentProps {
  /**
   * Endpoint générique des individus d'acte. Quand il est fourni,
   * l'enregistrement passe par `PUT {urlIndividus}/{id}` au lieu du PUT du
   * dossier de numérisation (qui n'existe pas pour tous les types d'acte).
   */
  urlIndividus?: string;
  /**
   * Affiche la section « Autre information » et son champ Profession (défaut
   * `true`, comportement naissance). `false` en reconnaissance : décision
   * produit, la profession n'y est ni saisie ni affichée sur la fiche.
   */
  showProfession?: boolean;
  open: boolean;
  /** Numéro du parent (1 ou 2) : la modal est la même, seule la clé du payload change. */
  numeroParent: 1 | 2;
  /**
   * Libellés avec article (« le père » / « la mère ») quand le type d'acte
   * impose des rôles, cf. `constants/rolesParentsParTypeActe`. Absent en
   * naissance : on retombe sur « le parent N ».
   */
  libellesParents?: Record<1 | 2, string>;
  /**
   * Verrouille la civilité, déduite du rôle en reconnaissance. Cohérent avec
   * l'assistant, qui la préremplit et la grise également.
   */
  disabledSexe?: boolean;
  /** Identifiant de la fiche (celui de l'acte), utilisé pour l'URL de `service.update`. */
  identifiant?: number;
  parent?: any;
  /** Date de naissance de l'enfant, pour le contrôle « le parent doit être né avant l'enfant ». */
  naissanceDateEnfant?: string;
  /** Instance `service` de `useFiche` fournie par `ActeFicheContainer` (celle de la lecture). */
  service?: { update: (data: Record<string, any>) => Promise<any> };
  onClose: () => void;
  onSaved: (parent: any) => void;
}

const CHAMPS_EDITABLES = [
  'nomFamille',
  'nomUsage',
  'prenom',
  'prenomsComplementaires',
  'sexe',
  'jourNaissance',
  'moisNaissance',
  'anneeNaissance',
  'adresseNaissance',
  'profession',
];

/**
 * `EtatCivil` est utilisé sans `attributePrefix` (contrairement au `ParentForm`
 * de la lib, qui émet des noms préfixés `parentN.`) : `data` porte donc les
 * champs à plat, ce qui permet de réutiliser le `handleChange` maison ET
 * `validationInformationsNaissance` telle quelle.
 */
const ModalParent = (props: ModalParentProps) => {
  const {
    open,
    numeroParent,
    libellesParents,
    disabledSexe = false,
    urlIndividus,
    showProfession = true,
    identifiant,
    parent,
    naissanceDateEnfant,
    service,
    onClose,
    onSaved,
  } = props;

  /** « le père », « la mère »… ou le rang si le type d'acte n'impose rien. */
  const roleParent =
    libellesParents?.[numeroParent] ?? `le parent ${numeroParent}`;

  const [data, setData] = React.useState<Record<string, any>>({});
  const [saving, setSaving] = React.useState(false);
  const [attempted, setAttempted] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setData(_.pick(parseIndividuFiche(parent), CHAMPS_EDITABLES));
    setAttempted(false);
  }, [open, parent]);

  const handleChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: unknown },
    ) => {
      const { name, value } = payload;
      setData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  /** validateur attend annee + mois + jour, sinon erreur (date incomplète) */
  const dateNaissanceEnfant = React.useMemo(() => {
    const triplet = parseDateNaissance(naissanceDateEnfant);
    return _.isNil(triplet.anneeNaissance)
      ? undefined
      : (triplet as {
          anneeNaissance: number;
          moisNaissance: number;
          jourNaissance: number;
        });
  }, [naissanceDateEnfant]);

  const errors = React.useMemo(
    () =>
      validationInformationsNaissance(data, true, false, dateNaissanceEnfant),
    [data, dateNaissanceEnfant],
  );

  const displayedErrors = attempted ? errors : {};

  const handleSave = React.useCallback(() => {
    setAttempted(true);
    if (!_.isEmpty(errors)) {
      return;
    }

    // Cf. ModalEnfant : `urlIndividus` (reconnaissance) passe par l'endpoint
    // générique de l'individu ; sinon (naissance) par le PUT du dossier, qui
    // recalcule `ai_sexel` et alimente le référentiel des professions.
    const payload = buildParentUpdatePayload(
      identifiant as number,
      numeroParent,
      parent,
      data,
    );
    const enregistrement = urlIndividus
      ? request.put(
          `${urlIndividus}/${parent?.identifiant}`,
          buildIndividuPutPayload(parent, data),
        )
      : service?.update(payload);

    // Ni endpoint d'individu, ni service : rien à quoi envoyer la modification.
    if (!enregistrement) {
      return;
    }

    setSaving(true);
    enregistrement
      .then(() => {
        onSaved({ ...parent, ...payload[`parent${numeroParent}`] });
        onClose();
      })
      .catch(() => {
        toast.error(
          `Une erreur est survenue lors de l'enregistrement de ${roleParent}`,
        );
      })
      .finally(() => {
        setSaving(false);
      });
  }, [
    errors,
    service,
    identifiant,
    numeroParent,
    parent,
    urlIndividus,
    data,
    roleParent,
    onSaved,
    onClose,
  ]);

  return (
    <Modal open={open} onClose={onClose} size="small">
      <Modal.Header content={`Modifier ${roleParent}`} />
      <Modal.Content>
        {/* @ts-ignore */}
        <Form>
          <EtatCivil
            isParent
            title="État civil"
            typeComponent={TypeComponent.NAISSANCE}
            disabledSexe={disabledSexe}
            individu={data}
            errors={displayedErrors}
            onChange={handleChange}
          />
          <ChampLieu
            data={data}
            errors={{ adresseNaissance: displayedErrors.adresseNaissance }}
            onChange={handleChange}
          />
          {showProfession && (
            <>
              {/* @ts-ignore */}
              <SectionHeader title="Divers" />
              <Grid>
                <Grid.Row style={{ paddingLeft: '1em' }}>
                  <Grid.Column computer={8} tablet={16} mobile={16}>
                    <ChampProfession
                      name="profession"
                      value={data?.profession}
                      onChange={handleChange}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </>
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          <Icon name="close" />
          Annuler
        </Button>
        <Button
          positive
          loading={saving}
          disabled={saving}
          onClick={handleSave}
        >
          <Icon name="check" />
          Enregistrer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModalParent;
